/**
 * When a manual-mode shadow map is re-rendered (SceneRig.setShadowUpdates "manual"). Pure timing, unit-tested
 * (test/shadowCadence.test.ts).
 *
 * The old rule reset its clock to 0 on each refresh instead of carrying the remainder, so every rate came out low and
 * stepped: 10 Hz ran every 7th frame at 60 fps (8.6 Hz), 30 Hz every 3rd (20 Hz). The clock now carries over, so a rate
 * that divides the frame rate lands exactly (30 Hz is every other frame at 60), and `EveryFrame` refreshes on every
 * rendered frame while something casts and moves.
 */
export class ShadowCadence {
  /** A rate that refreshes on every rendered frame. */
  static readonly EveryFrame = Infinity;
  /**
   * Seconds of slack under the period that still count as due: rAF steps jitter by a millisecond or so, and a frame
   * landing just short of its slot would otherwise slip a whole frame.
   */
  static readonly EarlySec = 0.002;
  private clock = 0;

  /** Forgets any partial period (nothing animates; the next refresh comes a full period after motion starts). */
  reset(): void { this.clock = 0; }

  /** Advances by `dt` seconds at `hz`; true when the map is due this frame. */
  step(dt: number, hz: number): boolean {
    if (!(hz > 0)) return false;
    if (!Number.isFinite(hz)) return true;
    const period = 1 / hz;
    this.clock += Math.max(0, dt);
    if (this.clock < period - ShadowCadence.EarlySec) return false;
    // carry the remainder, but a stall's backlog is dropped (it does not buy a burst of refreshes)
    this.clock = Math.max(0, this.clock - period);
    if (this.clock >= period - ShadowCadence.EarlySec) this.clock = 0;
    return true;
  }
}

/**
 * The hub's shadow rate per render-quality tier (owner 2026-10-24: "the shadows on the home screen look to still be
 * doing 20fps"). The hub's casters really move — the fortress companions fly laps, pets and critters walk the lawn,
 * the towers' weapons look about — so a throttled map visibly steps behind them. Measured on the hub at q=high
 * (540×675, 12 animated casters): the main pass is ~143 draws / 362 k triangles, a shadow pass adds ~68 draws /
 * 163 k triangles, depth only. Refreshing on every frame costs ~+50 % draws on the hub, which is still well under a
 * battle frame's, so every tier with shadows refreshes on every rendered frame except `low` (the tier the governor
 * drops to on a struggling phone), which takes every other frame (30 Hz at 60).
 */
export function hubShadowHz(tierName: string): number {
  return tierName === "low" ? HUB_LOW_TIER_SHADOW_HZ : ShadowCadence.EveryFrame;
}
/** The `low` tier's hub shadow rate: every other frame at 60 fps. */
export const HUB_LOW_TIER_SHADOW_HZ = 30;

/** The rig's manual shadow switches the hub drives (SceneRig has them). */
interface ShadowRig {
  readonly tierName: string;
  animatedCasters: number;
  setShadowUpdates(mode: "auto" | "manual", animatedHz?: number): void;
  setShadowHz(hz: number): void;
}

/**
 * The hub's shadow map on the rig: manual updates at `hubShadowHz` while `casters` animate (the companions, pets and
 * critters move every frame, so the map follows them on every rendered frame), the rate kept on the tier the governor
 * picks, and automatic updates back when the hub closes.
 */
export const HubShadows = {
  start(rig: ShadowRig, casters: number): void { rig.setShadowUpdates("manual", hubShadowHz(rig.tierName)); rig.animatedCasters = casters; },
  follow(rig: ShadowRig): void { rig.setShadowHz(hubShadowHz(rig.tierName)); },
  stop(rig: ShadowRig): void { rig.setShadowUpdates("auto"); rig.animatedCasters = 0; },
} as const;
